import {
  type Dispatch,
  type FC,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useState,
} from 'react'
import { useIntl } from 'react-intl'

import { useActivateContentsquareExperiment } from 'contentsquare'

import { EXPERIMENT_NAME_DIRECT_CONTACT_FORM_DIALOG } from '../../../experiments/model/EXPERIMENT_NAME'
import { type OpenableControls } from '../../../generic-hooks'

import { useDirectFormDialogContext } from './DirectContactFormDialogContext'
import { DirectContactFormAccountDeletion } from './DirectContactFormForms/DirectContactFormAccountDeletion'
import { DirectContactFormAdvertising } from './DirectContactFormForms/DirectContactFormAdvertising'
import { DirectContactFormCancelOrder } from './DirectContactFormForms/DirectContactFormCancelOrder'
import { DirectContactFormChangeAddress } from './DirectContactFormForms/DirectContactFormChangeAddress'
import { DirectContactFormChangeName } from './DirectContactFormForms/DirectContactFormChangeName'
import { DirectContactFormChangeOrder } from './DirectContactFormForms/DirectContactFormChangeOrder'
import { DirectContactFormDefaultCom } from './DirectContactFormForms/DirectContactFormDefaultCom'
import { DirectContactFormDeferPayment } from './DirectContactFormForms/DirectContactFormDeferPayment'
import { DirectContactFormInvoiceStatus } from './DirectContactFormForms/DirectContactFormInvoiceStatus'
import { DirectContactFormMissingInvoice } from './DirectContactFormForms/DirectContactFormMissingInvoice'
import { DirectContactFormOrderMissingOrIncorrectItem } from './DirectContactFormForms/DirectContactFormOrderMissingOrIncorrectItem'
import { DirectContactFormOtherTopics } from './DirectContactFormForms/DirectContactFormOtherTopics'
import { DirectContactFormPaymentReminder } from './DirectContactFormForms/DirectContactFormPaymentReminder'
import { DirectContactFormPharma } from './DirectContactFormForms/DirectContactFormPharma'
import { DirectContactFormReturnItem } from './DirectContactFormForms/DirectContactFormReturnItem'
import { DirectContactFormTrackDeliveryOrder } from './DirectContactFormForms/DirectContactFormTrackDeliveryOrder'
import { DirectContactFormReportOrder } from './DirectContactFormForms/DirectContactReportOrder'

const subjectComponentMap = (
  intl: ReturnType<typeof useIntl>,
  onFormSubmittedWithSuccess: () => void,
  userModalControls: OpenableControls,
): Record<string, ReactElement> => ({
  [intl.formatMessage({ id: 'direct.contact.form.advertising' })]: (
    <DirectContactFormAdvertising
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.change.address' })]: (
    <DirectContactFormChangeAddress
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.change.name' })]: (
    <DirectContactFormChangeName
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.change.order' })]: (
    <DirectContactFormChangeOrder
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.defer.payment' })]: (
    <DirectContactFormDeferPayment
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.delete.account' })]: (
    <DirectContactFormAccountDeletion
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.invoice.status' })]: (
    <DirectContactFormInvoiceStatus
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.missing.invoice' })]: (
    <DirectContactFormMissingInvoice
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.order.cancellation' })]: (
    <DirectContactFormCancelOrder
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({
    id: 'direct.contact.form.order.missing.or.incorrect.item',
  })]: (
    <DirectContactFormOrderMissingOrIncorrectItem
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),

  [intl.formatMessage({ id: 'direct.contact.form.other.concern' })]: (
    <DirectContactFormOtherTopics
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.payment.reminder' })]: (
    <DirectContactFormPaymentReminder
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.pharma.advice' })]: (
    <DirectContactFormPharma
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.report.order' })]: (
    <DirectContactFormReportOrder
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.return.order' })]: (
    <DirectContactFormReturnItem
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
  [intl.formatMessage({ id: 'direct.contact.form.track.delivery.order' })]: (
    <DirectContactFormTrackDeliveryOrder
      onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
      userModalControls={userModalControls}
    />
  ),
})

const getComponentForSubject = ({
  intl,
  onFormSubmittedWithSuccess,
  subject,
  userModalControls,
}: {
  intl: ReturnType<typeof useIntl>
  onFormSubmittedWithSuccess: () => void
  subject: string
  userModalControls: OpenableControls
}): ReactElement => {
  const components = subjectComponentMap(
    intl,
    onFormSubmittedWithSuccess,
    userModalControls,
  )

  return (
    components[subject] ?? (
      <DirectContactFormDefaultCom
        onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
        userModalControls={userModalControls}
      />
    )
  )
}

export const DirectContactFormDialogContent: FC<{
  clearSubjectOnClose: boolean
  onFormSubmittedWithSuccess: () => void
  setClearSubjectOnClose: Dispatch<SetStateAction<boolean>>
  userModalControls: OpenableControls
}> = ({
  clearSubjectOnClose,
  onFormSubmittedWithSuccess,
  setClearSubjectOnClose,
  userModalControls,
}) => {
  const { formSubject, setSubject, setUserData, subject } =
    useDirectFormDialogContext()
  const [useFormSubject, setUseFormSubject] = useState(true)
  const intl = useIntl()

  useActivateContentsquareExperiment(EXPERIMENT_NAME_DIRECT_CONTACT_FORM_DIALOG)

  useEffect(() => {
    if (formSubject && useFormSubject) {
      setSubject((prevState) =>
        prevState === formSubject ? prevState : formSubject,
      )
      setUseFormSubject(false)
    }

    if (clearSubjectOnClose) {
      setSubject('')
      setClearSubjectOnClose(false)
      setUserData({})
    }
  }, [
    clearSubjectOnClose,
    formSubject,
    setClearSubjectOnClose,
    setSubject,
    setUserData,
    subject,
    useFormSubject,
  ])

  return getComponentForSubject({
    intl,
    onFormSubmittedWithSuccess,
    subject,
    userModalControls,
  })
}
